import nxModule from 'nxModule';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/combineAll';
import {BranchService} from 'components/service/branch.service';
import {CustomerProfile} from 'components/customer/profile/customer-profile.types';
import {ILocationService, IScope} from 'angular';
import {HttpService} from 'shared/utils/httpService';
import Authentication from 'shared/utils/authentication';
import templateUrl from './transfer-customers-to-another-group.template.html';
import {Branch} from "management/BranchTypes";
import notification from 'shared/utils/notification';
import './transfer-customers-to-another-group.component.less';
import {TransferCustomerDataServiceType} from "components/customer/group-loans/transfer-customers-to-another-group/transfer-customer-data.service";
import { Confirmation } from 'shared/common/confirmation.types';

interface CustomersGroupInfo {
  name: string;
  branchId: number;
  groupCustomerId: number;
}

class TransferCustomersToAnotherGroup {
  private readonly subHeaderDescription = 'Transfer customer/s';

  /**
   * Customer ID of the original group (CustomerType: GROUP)
   * @private
   */
  private groupCustomerId!: number;

  private selectableBranches: Branch[] = [];
  private selectableGroups: CustomersGroupInfo[] = [];
  private groupsByBranchId: Map<number, CustomersGroupInfo[]> = new Map;

  private successTransfers: string[] = [];
  private failedTransfers: { name: string; error: any }[] = [];

  private targetBranchId!: number;
  private targetCustomerGroupId!: number;

  constructor(private readonly $scope: IScope,
              private readonly command: { execute: any },
              private readonly $location: ILocationService,
              private readonly authentication: Authentication,
              private readonly branchService: BranchService,
              private readonly http: HttpService,
              private readonly $route: { current: any },
              private readonly customerCache: { profile: any, refetch(): any },
              private readonly groupCustomerCache: { refetch(): any },
              private notification: notification,
              private transferCustomerDataService: TransferCustomerDataServiceType,
              private confirmation : Confirmation) {
  }

  async $onInit(): Promise<void> {
    this.groupCustomerId = Number(this.$route.current.params['groupCustomerId']);
    [this.selectableBranches] = await Promise.all([this.branchService.toPromise()]);
    await this.onBranchChange();
  }

  async onBranchChange(): Promise<void> {
    await this.fetchMissingGroups();
    this.selectableGroups = this.getGroups(this.targetBranchId);
  }

  async fetchMissingGroups(): Promise<void> {
    if (!this.targetBranchId || this.groupsByBranchId.has(this.targetBranchId)) {
      return;
    }

    const groups: CustomersGroupInfo[] = await <Promise<CustomersGroupInfo[]>>this.http.get(`/customers/group/all?branchId=${this.targetBranchId}`).toPromise();
    const groupsWithoutCurrentOne = (groups || []).filter(g => g.groupCustomerId !== this.groupCustomerId);
    this.groupsByBranchId.set(this.targetBranchId, groupsWithoutCurrentOne);
  }

  getGroups(branchId: number): CustomersGroupInfo[] {
    const result = this.groupsByBranchId.get(branchId);
    return result ?? [];
  }

  async transfer(): Promise<void> {
    if (!Array.isArray(this.transferCustomerDataService.data) || this.transferCustomerDataService.data.length === 0) {
      this.notification.show('No customer selected for transfer found.');
      return;
    }

    const shouldTransfer = await this.confirmation('Proceed with the transfer of the selected members to the target branch and group?');

    if (!shouldTransfer) {
      return;
    }

    await this.processTransfers(this.transferCustomerDataService.data);

    if (this.failedTransfers.length > 0) {
      this.notification.show(
          'Customer Transfer Failed',
          `${this.failedTransfers.length} transfer(s) failed.`,
          5000
      );
    }
    if (this.successTransfers.length > 0) {
      this.notification.show(
          'Customer Transfer Success',
          `${this.successTransfers.length} transfer(s) succeeded.`,
          5000
      );
    }

    this.transferCustomerDataService.data = [];

    if (this.failedTransfers.length === 0) {
      this.redirectBack();
    }
  }

  private async processTransfers(customerIds: number[]): Promise<void> {
    for (const customerId of customerIds) {
      const customerProfile: CustomerProfile = await this.customerCache.profile(customerId).toPromise();
      const name = customerProfile.effectiveName;

      try {
        const response = await this.command
            .execute('TransferCustomerToAnotherGroup', {
              customerId: customerId,
              sourceGroupCustomerId: this.groupCustomerId,
              targetGroupCustomerId: this.targetCustomerGroupId,
            })
            .toPromise();

        if (response) {
          this.successTransfers.push(name);
        } else {
          throw new Error('Transfer command returned no response.');
        }
      } catch (error) {
        console.error(`Failed to transfer customer ID ${customerId}:`, error);
        this.failedTransfers.push({
          name: name,
          error: error.message || 'Unknown error',
        });
      }
    }

    await Promise.all([this.groupCustomerCache.refetch(), this.customerCache.refetch()]);
  }

  public hasNoCustomerToTransfer(): boolean {
    const data = this.transferCustomerDataService.data;
    return !Array.isArray(data) || data.length === 0;
  }

  /**
   * Moves back to origin group customer
   */
  redirectBack(): void {
    this.$location.path(`/customer/${this.groupCustomerId}/group-profile`);
  }
}

nxModule.component('transferCustomersToAnotherGroup', {
  templateUrl: templateUrl,
  controller: TransferCustomersToAnotherGroup
});
